import { createFileRoute, Link } from '@tanstack/react-router';
import { useProperty } from '@/lib/property/propertyContextDef';
import { useBookingByReference, useBookingEvents, guestName, listingTitle } from '@/lib/data/bookings';
import { computeBookingMoney, inAppPaidLabel } from '@/lib/money/bookingMoney';
import { formatEurCents, formatNad } from '@/lib/money/format';
import { StatusPill, FlagBadges } from '@/lib/bookings/StatusPill';
import { HostCancelBooking } from '@/lib/bookings/HostCancelBooking';
import { UndoNoShow } from '@/lib/bookings/UndoNoShow';
import { PrintIcon } from '@/icons';
import { parseDeviceLabel } from '@/lib/desk/events';
import { PageLoading } from '@/ui/Loader';

export const Route = createFileRoute('/bookings/$reference/')({
  component: BookingDetail,
});

const EVENT_LABELS: Record<string, string> = {
  'checkin.recorded': 'Checked in',
  // What desk_check_in actually writes (Task 12's SQL) — Task 14.
  checked_in: 'Checked in',
  'balance.recorded': 'Balance received',
  'balance.undone': 'Balance entry undone',
  'status.changed': 'Status changed',
  'refund.processed': 'Fee refunded',
  'amendment.applied': 'Booking amended',
  'no_show.recorded': 'Marked no-show',
  // What sweep_no_shows / admin_booking_action / host_undo_no_show write.
  no_show: 'Marked no-show',
  'no_show.undone': 'No-show undone',
};

function eventLabel(event: string): string {
  return EVENT_LABELS[event] ?? event;
}

function BookingDetail() {
  const { reference } = Route.useParams();
  const { businessId, role } = useProperty();
  const { data: booking, isLoading, error } = useBookingByReference(businessId, reference);
  const { data: events } = useBookingEvents(booking?.id);

  if (isLoading) return <PageLoading />;
  if (error) return <p className="text-body text-danger">Couldn't load this booking: {(error as Error).message}</p>;
  if (!booking) {
    return (
      <section className="rounded-card border border-border bg-surface p-xl shadow-card">
        <h1 className="text-screen-title font-extrabold tracking-tight text-ink">Booking not found</h1>
        <p className="mt-sm text-body text-ink-2">
          No booking with reference "{reference}" on this property.{' '}
          <Link to="/bookings" className="text-canyon underline">
            Back to bookings
          </Link>
        </p>
      </section>
    );
  }

  const money = computeBookingMoney({
    totalPrice: booking.total_price,
    depositAmount: booking.deposit_amount,
    balanceRecordedAmountNad: booking.balance_recorded_amount_nad,
    feeStatus: booking.fee_status,
    balanceStatus: booking.balance_status,
  });

  return (
    <section className="flex flex-col gap-lg">
      <div className="flex flex-wrap items-center justify-between gap-md">
        <div>
          <h1 className="text-screen-title font-extrabold tracking-tight text-ink">
            {booking.reference_number ?? booking.id}
          </h1>
          <div className="mt-xs flex items-center gap-sm">
            <StatusPill status={booking.display_status} />
            <FlagBadges flags={booking.display_flags} />
          </div>
        </div>
        <Link
          to="/bookings/$reference/print"
          params={{ reference }}
          target="_blank"
          className="inline-flex items-center gap-xs rounded-button border border-border bg-surface px-md py-xs text-body text-ink"
        >
          <PrintIcon className="h-4 w-4" aria-hidden="true" />
          Print
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-lg md:grid-cols-2">
        <div className="rounded-card border border-border bg-surface p-lg shadow-card">
          <h2 className="text-h3 font-bold text-ink">Guest &amp; stay</h2>
          <dl className="mt-md grid grid-cols-2 gap-y-sm text-body">
            <dt className="text-ink-2">Guest</dt>
            <dd className="text-ink">{guestName(booking)}</dd>
            <dt className="text-ink-2">Listing</dt>
            <dd className="text-ink">{listingTitle(booking)}</dd>
            <dt className="text-ink-2">Check-in</dt>
            <dd className="text-ink">{booking.check_in ?? '—'}</dd>
            <dt className="text-ink-2">Check-out</dt>
            <dd className="text-ink">{booking.check_out ?? '—'}</dd>
            <dt className="text-ink-2">Party</dt>
            <dd className="text-ink">{booking.guests ?? '—'}</dd>
            {booking.notes ? (
              <>
                <dt className="text-ink-2">Notes</dt>
                <dd className="text-ink">{booking.notes}</dd>
              </>
            ) : null}
          </dl>
        </div>

        <div className="rounded-card border border-border bg-surface p-lg shadow-card">
          <h2 className="text-h3 font-bold text-ink">Money</h2>
          <dl className="mt-md grid grid-cols-2 gap-y-sm text-body">
            <dt className="text-ink-2">Total</dt>
            <dd className="text-ink">{formatNad(money.totalCents / 100)}</dd>
            <dt className="text-ink-2">{inAppPaidLabel(money)}</dt>
            <dd className="text-ink">
              {formatNad(money.feePaidCents / 100)}
              {booking.fee_eur_cents != null ? (
                <span className="text-ink-2"> ({formatEurCents(booking.fee_eur_cents)})</span>
              ) : null}
            </dd>
            <dt className="text-ink-2">Balance due</dt>
            <dd className="font-semibold text-ink">{formatNad(money.balanceDueCents / 100)}</dd>
            <dt className="text-ink-2">Balance status</dt>
            <dd className="text-ink capitalize">{booking.balance_status.replace(/_/g, ' ')}</dd>
            {booking.balance_method ? (
              <>
                <dt className="text-ink-2">Balance method</dt>
                <dd className="text-ink capitalize">{booking.balance_method.replace(/_/g, ' ')}</dd>
              </>
            ) : null}
          </dl>
        </div>
      </div>

      <UndoNoShow
        businessId={businessId}
        bookingId={booking.id}
        reference={reference}
        noShowAt={booking.no_show_at}
        role={role}
      />

      <HostCancelBooking
        businessId={businessId}
        bookingId={booking.id}
        reference={reference}
        status={booking.status}
        role={role}
      />

      <div className="rounded-card border border-border bg-surface p-lg shadow-card">
        <h2 className="text-h3 font-bold text-ink">Event timeline</h2>
        {!events ? (
          <PageLoading compact />
        ) : events.length === 0 ? (
          <p className="mt-sm text-body text-ink-2">No events recorded yet.</p>
        ) : (
          <ol className="mt-md flex flex-col gap-md border-l border-hair pl-md">
            {events.map((event) => (
              <li key={event.id} className="relative">
                <span className="absolute -left-[calc(0.75rem+1px)] top-1 h-2 w-2 rounded-full bg-canyon" aria-hidden="true" />
                <p className="text-body font-semibold text-ink">{eventLabel(event.event)}</p>
                <p className="text-caption text-ink-2">
                  {new Date(event.created_at).toLocaleString()} · {event.actor_type}
                  {event.amount_nad != null ? ` · ${formatNad(event.amount_nad)}` : ''}
                  {event.method ? ` · ${event.method.replace(/_/g, ' ')}` : ''}
                </p>
                <DeskCaptureNote device={event.device} syncedAt={event.created_at} />
              </li>
            ))}
          </ol>
        )}
      </div>
    </section>
  );
}

/** Desk events captured at the check-in desk carry the capture time in
 * `device` (Task 14), so an offline check-in shows both when it was captured
 * and when it reached NamibiaGo. */
function DeskCaptureNote({ device, syncedAt }: { device: string | null; syncedAt: string }) {
  const capture = parseDeviceLabel(device);
  if (!capture) return null;
  return (
    <p className="text-caption text-ink-2" data-testid="desk-capture-note">
      Captured at the desk {new Date(capture.capturedAt).toLocaleString()}
      {capture.offline ? ` (offline) · reached NamibiaGo ${new Date(syncedAt).toLocaleString()}` : ''}
    </p>
  );
}
